/**
 * Format a price so that at least four **significant** digits are
 * visible, regardless of magnitude. The prior implementation capped at
 * 6 decimals which collapsed everything below ~1e-6 to "0.000000" —
 * DEX dust tokens (e.g. EUSETH at 2.3e-7) were unreadable. Now:
 *
 *   p ≥ 1000    → 1234.56
 *   p ≥ 100     → 123.4567
 *   p ≥ 1       → 1.23456
 *   p ≥ 0.01    → 0.123456
 *   p ≥ 0.0001  → 0.01234567
 *   smaller     → "0.0[N]…" compact notation when there are 5+ leading
 *                 zeros; otherwise enough decimals to include 4 sig digs
 *                 past the first non-zero, capped at 14 to avoid
 *                 floating-point tail garbage.
 *
 * Compact example (DexScreener-style):
 *   0.0000002300 → "0.0[5]2300"
 *   (5 = count of additional zeros after the first "0." zero)
 *
 * Keeps a fixed number of decimals / consistent brackets so two prices
 * in a column line up digit-by-digit — important for ``PriceStack``'s
 * first-differing-digit highlight.
 */
export function fmtPrice(p: number): string {
  if (!isFinite(p)) return "—";
  if (p === 0) return "0";
  const abs = Math.abs(p);
  if (abs >= 1000)   return p.toFixed(2);
  if (abs >= 100)    return p.toFixed(4);
  if (abs >= 1)      return p.toFixed(5);
  if (abs >= 0.01)   return p.toFixed(6);
  if (abs >= 0.0001) return p.toFixed(8);

  // Very small — compute enough decimals to expose 4 significant digits
  // past the leading run of zeros. Example: 2.3e-7 has 6 leading zeros
  // in its decimal expansion → need 6 + 4 = 10 decimals → "0.00000023".
  const leadingZeros = -Math.floor(Math.log10(abs)) - 1;
  const decimals = Math.min(14, leadingZeros + 4);
  const raw = p.toFixed(decimals);
  return compactLeadingZeros(raw);
}

/**
 * Collapse a run of leading zeros in the fractional part into a
 * bracketed count, à la DexScreener:
 *
 *   "0.000000230000" → "0.0[5]230000"   (6 leading zeros in fraction
 *                                         → 1 shown + [5] bracketed)
 *   "0.00023"        → "0.00023"        (3 leading zeros, not enough
 *                                         to save space → left alone)
 *
 * Threshold: 5+ consecutive zeros. "[N]" eats 3–4 chars of its own, so
 * compacting only below that threshold would either shrink nothing
 * (N=4 → same length) or lose clarity (N=3 → longer). At 5+ we save
 * at least 2 chars per price and the saving grows with magnitude.
 */
function compactLeadingZeros(raw: string): string {
  const m = raw.match(/^(-?)0\.(0+)(\d+)$/);
  if (!m) return raw;
  const [, sign, zeros, rest] = m;
  if (zeros.length < 5) return raw;
  return `${sign}0.0[${zeros.length - 1}]${rest}`;
}

export function fmtAge(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return "—";
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m || "<1"}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h${rem}m` : `${h}h`;
}

export function fmtAgeMinutes(m: number): string {
  if (!isFinite(m) || m < 0) return "—";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h${rem}m` : `${h}h`;
}

/** Dump/pump % with 1 decimal, truncated TOWARD ZERO so a near-total move
 *  like -99.98 renders "-99.9%", not the misleading "-100.0%" that
 *  ``toFixed(1)`` (which rounds) produces. Mirrors the bot's ``_fmt_pct``
 *  in src/dex_dump/telegram.py. */
export function fmtMovePct(v: number): string {
  if (!isFinite(v)) return "—";
  const t = Math.trunc(v * 10) / 10;
  return `${t > 0 ? "+" : ""}${t.toFixed(1)}%`;
}

export function fmtVol(v: number): string {
  if (!isFinite(v)) return "—";
  if (v >= 10000) return `${Math.floor(v / 1000)}k`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return `${Math.round(v)}`;
}

/** Dust-safe price WITHOUT the compact "[N]" bracket notation — the form the
 *  ladder screens (Premarket/Funding) and the Spreads table use. ≥1000→2dp,
 *  ≥1→4dp, ≥0.001→6dp, else exponential. "—" for null/non-finite/≤0.
 *  (Distinct from ``fmtPrice``, which adds the DexScreener "0.0[N]" compaction.) */
export function fmtPx(p?: number | null): string {
  if (p == null || !isFinite(p) || p <= 0) return "—";
  if (p >= 1000) return p.toFixed(2);
  if (p >= 1) return p.toFixed(4);
  if (p >= 0.001) return p.toFixed(6);
  return p.toExponential(2);
}

/** USD amount in $K/M/B (1dp for B/M, 0dp for K). "—" for null/non-finite/≤0.
 *  Distinct from ``fmtVol`` (bare k-notation, no "$"). */
export function fmtUsd(v?: number | null): string {
  if (v == null || !isFinite(v) || v <= 0) return "—";
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

export function fmtProfit(p: number): string {
  if (!isFinite(p)) return "—";
  if (p >= 1000) return `$${(p / 1000).toFixed(1)}k`;
  return `$${Math.round(p)}`;
}

export function fmtPct(n: number, digits = 2): string {
  if (!isFinite(n)) return "—";
  return `${n.toFixed(digits)}%`;
}

/** Wall distance-from-mid: bps → human %. Sub-1% keeps 2 decimals (0.38%) so
 *  tight walls stay distinguishable; ≥1% drops to 1 decimal (1.5%, 4.3%) for
 *  compactness. Anything under 0.01% collapses to "0%". */
export function fmtDistPct(bps?: number | null): string {
  if (bps == null || !isFinite(bps) || bps < 1) return "0%";
  const pct = bps / 100;
  return pct < 1 ? `${pct.toFixed(2)}%` : `${pct.toFixed(1)}%`;
}
